import { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle, Loader2, RotateCcw } from "lucide-react";
import client, { formatApiError } from "@/lib/api";
import { Panel } from "@/components/widgets";
import { Button } from "@/components/ui/button";
import { fmtKg, fmtInt } from "@/lib/format";
import { toast } from "sonner";

function StatusDot({ status }) {
  const map = { valid: ["#DCFCE7", "#15803D", "Valid"], warning: ["#FEF3C7", "#B45309", "Warning"], rejected: ["#FEE2E2", "#B91C1C", "Rejected"] };
  const [bg, c, label] = map[status] || map.valid;
  return <span className="text-xs font-medium px-2 py-0.5 rounded" style={{ background: bg, color: c }}>{label}</span>;
}

export default function DataImport() {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [recon, setRecon] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setResult(null); setRecon(null); if (fileRef.current) fileRef.current.value = ""; };

  const validate = async () => {
    if (!file) return;
    setBusy(true); setResult(null); setRecon(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await client.post("/imports/nesting-workbook", fd);
      setResult(data.data);
      toast.success("Workbook validated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Validation failed");
    } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/imports/${result.importBatchId}/commit`);
      setRecon(data.data.reconciliation);
      toast.success(`Imported ${data.data.reconciliation.importedRows} records`);
      qc.invalidateQueries();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Commit failed");
    } finally { setBusy(false); }
  };

  const s = result?.summary;
  const importable = s ? s.validRows + s.warningRows : 0;

  return (
    <Panel testid="import-panel">
      <div className="flex items-start gap-2 text-xs text-slate-600 bg-slate-50 border rounded-lg px-4 py-3 mb-5" style={{ borderColor: "#E2E8F0" }}>
        <FileSpreadsheet size={16} className="text-violet-700 shrink-0 mt-0.5" />
        <span>Upload a LASER WEIGHT workbook (.xlsx). Expected columns: <b>Date, Customer, Purchase Order / Job Ref, Programmed By, Material Grade, Material, Thickness (mm), Machine, Weight (kg)</b>. Rows are validated first; you review the summary, then commit. New customers, programmers, grades, ownership and thicknesses are created automatically on commit.</span>
      </div>

      {!recon && (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-5">
          <label className="flex-1 flex items-center gap-3 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer hover:border-violet-400 transition-colors"
            style={{ borderColor: "#CBD5E1" }} data-testid="import-dropzone">
            <Upload size={18} className="text-slate-400" />
            <span className="text-sm text-slate-600 truncate">{file ? file.name : "Choose an .xlsx workbook…"}</span>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden"
              onChange={(e) => { setFile(e.target.files[0]); setResult(null); setRecon(null); }} data-testid="import-file-input" />
          </label>
          <Button onClick={validate} disabled={!file || busy} className="text-white" style={{ background: "#7C3AED" }} data-testid="import-validate-btn">
            {busy && !result ? <Loader2 className="animate-spin" size={16} /> : "Validate"}
          </Button>
        </div>
      )}

      {/* Reconciliation result */}
      {recon && (
        <div className="space-y-4" data-testid="import-reconciliation">
          <div className="flex items-center gap-2 text-emerald-700 font-semibold"><CheckCircle2 size={20} /> Import committed</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <ReconCard label="Imported Rows" value={fmtInt(recon.importedRows)} />
            <ReconCard label="Imported Weight" value={`${fmtKg(recon.importedWeightKg)} kg`} />
            <ReconCard label="Rejected Rows" value={fmtInt(recon.rejectedRows)} />
            <ReconCard label="Reconciled" value={recon.weightReconciled ? "✓ Match" : "⚠ Review"} accent={recon.weightReconciled ? "#15803D" : "#B45309"} />
          </div>
          <Button variant="outline" onClick={reset} data-testid="import-again-btn"><RotateCcw size={15} className="mr-1.5" /> Import another workbook</Button>
        </div>
      )}

      {/* Validation summary */}
      {result && !recon && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <ReconCard label="Total Rows" value={fmtInt(s.totalRows)} />
            <ReconCard label="Valid" value={fmtInt(s.validRows)} accent="#15803D" />
            <ReconCard label="Warnings" value={fmtInt(s.warningRows)} accent="#B45309" />
            <ReconCard label="Rejected" value={fmtInt(s.rejectedRows)} accent="#B91C1C" />
          </div>

          <div className="flex gap-2">
            <Button onClick={commit} disabled={busy || importable === 0} className="text-white" style={{ background: "#0D9488" }} data-testid="import-commit-btn">
              {busy ? <Loader2 className="animate-spin" size={16} /> : `Commit ${importable} rows`}
            </Button>
            <Button variant="outline" onClick={reset} data-testid="import-cancel-btn">Cancel</Button>
          </div>

          {result.rejections?.length > 0 && (
            <div className="text-xs bg-red-50 border rounded-lg p-3" style={{ borderColor: "#FCA5A5" }}>
              <p className="font-semibold text-red-700 flex items-center gap-1 mb-1"><XCircle size={13} /> Rejected rows (excluded from import)</p>
              {result.rejections.slice(0, 8).map((r) => (
                <p key={r.row} className="text-red-600">Row {r.row}: {r.issues.map((i) => i.message).join("; ")}</p>
              ))}
            </div>
          )}
          {result.warnings?.length > 0 && (
            <div className="text-xs bg-amber-50 border rounded-lg p-3" style={{ borderColor: "#FDE68A" }}>
              <p className="font-semibold text-amber-700 flex items-center gap-1 mb-1"><AlertTriangle size={13} /> Warnings (will still import)</p>
              {result.warnings.slice(0, 8).map((w, i) => (<p key={i} className="text-amber-700">Row {w.row}: {w.message}</p>))}
            </div>
          )}

          <div className="overflow-x-auto border rounded-lg" style={{ borderColor: "#E2E8F0" }}>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500 border-b bg-slate-50 whitespace-nowrap" style={{ borderColor: "#E2E8F0" }}>
                <th className="py-2 px-3 font-medium">Row</th><th className="py-2 px-3 font-medium">Status</th>
                <th className="py-2 px-3 font-medium">Date</th><th className="py-2 px-3 font-medium">Customer</th>
                <th className="py-2 px-3 font-medium">Job Ref</th><th className="py-2 px-3 font-medium">Programmer</th>
                <th className="py-2 px-3 font-medium">Grade</th><th className="py-2 px-3 font-medium">Ownership</th>
                <th className="py-2 px-3 font-medium text-right">Thk</th><th className="py-2 px-3 font-medium">Machine</th>
                <th className="py-2 px-3 font-medium text-right">Weight</th>
              </tr></thead>
              <tbody>
                {result.preview.map((r) => (
                  <tr key={r.rowNum} className="border-b whitespace-nowrap" style={{ borderColor: "#F1F5F9" }} data-testid={`import-row-${r.rowNum}`}>
                    <td className="py-2 px-3 font-mono text-xs text-slate-400">{r.rowNum}</td>
                    <td className="py-2 px-3"><StatusDot status={r.status} /></td>
                    <td className="py-2 px-3 text-slate-600">{r.data.date || "—"}</td>
                    <td className="py-2 px-3 truncate max-w-[150px]">{r.data.customer || "—"}</td>
                    <td className="py-2 px-3 font-mono text-xs">{r.data.jobRef || "—"}</td>
                    <td className="py-2 px-3 text-slate-600">{r.data.programmer || "—"}</td>
                    <td className="py-2 px-3 text-xs text-slate-500">{r.data.grade || "—"}</td>
                    <td className="py-2 px-3 text-xs text-slate-500">{r.data.ownership || "—"}</td>
                    <td className="py-2 px-3 font-mono text-xs text-right">{r.data.thicknessMm ? `${r.data.thicknessMm} mm` : "—"}</td>
                    <td className="py-2 px-3 font-mono text-xs">{r.data.machineCode || "—"}</td>
                    <td className="py-2 px-3 font-mono text-right">{fmtKg(r.data.weight)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Panel>
  );
}

function ReconCard({ label, value, accent = "#0F172A" }) {
  return (
    <div className="panel p-3">
      <p className="text-[11px] text-slate-400 uppercase tracking-wide">{label}</p>
      <p className="kpi-value text-xl mt-1" style={{ color: accent }}>{value}</p>
    </div>
  );
}
